import { jsPDF } from "jspdf";

function fmt(value) {
  return new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 }).format(Number(value || 0));
}

function formatDateTime(value) {
  const date = value ? new Date(value) : new Date();

  if (Number.isNaN(date.getTime())) {
    return String(value || "N/D");
  }

  return date.toLocaleString("es-MX", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function dateStamp() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}${mm}${dd}`;
}

function defaultFolio() {
  return `REQ-PVC-${dateStamp()}-001`;
}

function labelEstado(value) {
  const map = {
    VENCIDA: "CRITICA",
    HOY: "PEDIR HOY",
    PREVENTIVA: "PREVENTIVA",
    OK: "COMPLEMENTARIA",
  };

  return map[value] || value || "N/D";
}

function labelInsumo(value) {
  const map = {
    plasticos: "PVC",
    ribbon: "Ribbon",
    laminador: "Laminador",
    retransfer: "Retransfer",
    rollos: "Rollo termico",
  };

  return map[value] || value || "N/D";
}

function getPedido(row) {
  return row?.pedidoSugerido || row?.pedido || {};
}

function pedidoLineas(row) {
  const pedido = getPedido(row);

  return [
    { key: "plasticos", label: "PVC", value: Number(pedido.plasticos || 0) },
    { key: "ribbon", label: "Ribbon", value: Number(pedido.ribbon || 0) },
    { key: "laminador", label: "Laminador", value: Number(pedido.laminador || 0) },
    { key: "retransfer", label: "Retransfer", value: Number(pedido.retransfer || 0) },
    { key: "rollos", label: "Rollo termico", value: Number(pedido.rollos || 0) },
  ].filter((item) => item.value > 0);
}

function tienePedido(row) {
  return pedidoLineas(row).length > 0;
}

function prioridad(row) {
  const map = {
    VENCIDA: 1,
    HOY: 2,
    PREVENTIVA: 3,
    OK: 4,
  };

  return map[row?.estado] || 9;
}

function getPrinterLabel(row) {
  const value = String(row?.printerLabel || row?.modelo || row?.impresora || "").toUpperCase();

  if (value.includes("K30")) {
    return "K30 SWITF PRO";
  }

  return "SMART 51L";
}

function pedidoTexto(row) {
  const lineas = pedidoLineas(row);

  if (!lineas.length) {
    return "Sin material solicitado";
  }

  return lineas.map((item) => `${item.label} ${fmt(item.value)}`).join("  |  ");
}

function observacionTexto(row) {
  const minimos = Array.isArray(row?.minimosOperativos) ? row.minimosOperativos : [];

  if (row?.motivoOperativo) {
    return row.motivoOperativo;
  }

  if (minimos.length > 0) {
    return "Bajo minimo: " + minimos.map(labelInsumo).join(", ");
  }

  if (row?.estado === "VENCIDA") {
    return "Riesgo critico de abasto";
  }

  if (row?.estado === "HOY") {
    return "Surtido inmediato";
  }

  if (row?.estado === "PREVENTIVA") {
    return "Reposicion preventiva";
  }

  return "Compra consolidada / kit cerrado";
}

function truncate(doc, text, width) {
  const value = String(text || "");

  if (doc.getTextWidth(value) <= width) {
    return value;
  }

  let next = value;

  while (next.length > 3 && doc.getTextWidth(next + "...") > width) {
    next = next.slice(0, -1);
  }

  return next + "...";
}

function textFit(doc, text, x, y, width, options = {}) {
  doc.text(truncate(doc, text, width), x, y, options);
}

function drawMeta(doc, x, y, label, value, width) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(5.9);
  doc.setTextColor(107, 114, 128);
  doc.text(String(label || "").toUpperCase(), x, y);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.4);
  doc.setTextColor(17, 24, 39);
  textFit(doc, value || "N/D", x, y + 4.8, width);
}

function drawMaterialTotal(doc, x, y, label, value, width) {
  doc.setFillColor(250, 250, 250);
  doc.setDrawColor(229, 231, 235);
  doc.roundedRect(x, y, width, 13, 2, 2, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(5.7);
  doc.setTextColor(107, 114, 128);
  doc.text(label.toUpperCase(), x + 3, y + 4.6);

  doc.setFontSize(10.5);
  doc.setTextColor(17, 24, 39);
  doc.text(fmt(value), x + 3, y + 10.2);
}

function setEstadoStyle(doc, estado) {
  if (estado === "VENCIDA") {
    doc.setFillColor(127, 29, 29);
    doc.setTextColor(255, 255, 255);
    return;
  }

  if (estado === "HOY") {
    doc.setFillColor(154, 52, 18);
    doc.setTextColor(255, 255, 255);
    return;
  }

  if (estado === "PREVENTIVA") {
    doc.setFillColor(133, 77, 14);
    doc.setTextColor(255, 255, 255);
    return;
  }

  doc.setFillColor(226, 232, 240);
  doc.setTextColor(51, 65, 85);
}

function drawPageBackground(doc, pageW, pageH) {
  doc.setFillColor(246, 247, 249);
  doc.rect(0, 0, pageW, pageH, "F");

  doc.setFillColor(255, 255, 255);
  doc.roundedRect(6, 6, pageW - 12, pageH - 12, 2, 2, "F");
}

function drawMainBanner(doc, pageW, margin, folio, isContinuation = false) {
  doc.setFillColor(20, 24, 36);
  doc.rect(6, 6, pageW - 12, 25, "F");

  doc.setFillColor(127, 29, 29);
  doc.rect(6, 6, 4, 25, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13.5);
  doc.setTextColor(255, 255, 255);
  doc.text("REQUISICION OPERATIVA DE INSUMOS PVC", margin + 5, 16);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(203, 213, 225);
  doc.text(
    isContinuation
      ? "Hoja de continuacion del detalle por oficina"
      : "Material requerido por oficina: promedio mensual + consumo estimado durante 4 días de lead time",
    margin + 5,
    23
  );

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.2);
  doc.setTextColor(255, 255, 255);
  doc.text("CONTROL DOCUMENTAL", 216, 13);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.9);
  doc.setTextColor(203, 213, 225);
  doc.text(`Folio: ${folio}`, 216, 19);
  doc.text(`Emitido: ${formatDateTime()}`, 216, 24.5);
}

function drawFirstPageMeta(doc, margin, usableW, totals, leadTimeDays, safetyPercent, traceability) {
  doc.setDrawColor(229, 231, 235);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(margin, 38, usableW, 21, 2, 2, "FD");

  drawMeta(doc, margin + 5, 46, "Ultima captura", formatDateTime(traceability?.ultimaCaptura), 45);
  drawMeta(doc, margin + 58, 46, "Responsable", traceability?.responsable || "Supervisor operativo", 44);
  drawMeta(doc, margin + 111, 46, "Origen", traceability?.origen || "Captura operativa", 42);
  drawMeta(doc, margin + 164, 46, "Lead time", `${leadTimeDays} dias`, 28);
  drawMeta(doc, margin + 202, 46, "Colchon logistico", `${Math.round(Number(safetyPercent || 0) * 100)}%`, 32);
  drawMeta(doc, margin + 243, 46, "Oficinas incluidas", fmt(totals.oficinas), 28);

  const totalsY = 66;
  const totalW = 43.2;

  drawMaterialTotal(doc, margin, totalsY, "PVC", totals.plasticos, totalW);
  drawMaterialTotal(doc, margin + 47, totalsY, "Ribbon", totals.ribbon, totalW);
  drawMaterialTotal(doc, margin + 94, totalsY, "Laminador", totals.laminador, totalW);
  drawMaterialTotal(doc, margin + 141, totalsY, "Retransfer", totals.retransfer, totalW);
  drawMaterialTotal(doc, margin + 188, totalsY, "Rollo termico", totals.rollos, totalW);

  doc.setFillColor(255, 247, 237);
  doc.setDrawColor(253, 186, 116);
  doc.roundedRect(margin + 235, totalsY, 43, 13, 2, 2, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(5.7);
  doc.setTextColor(154, 52, 18);
  doc.text("CRITERIO", margin + 238, totalsY + 4.6);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.2);
  doc.text("Kit cerrado / stock real", margin + 238, totalsY + 9.8);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(127, 29, 29);
  doc.text("DETALLE POR OFICINA", margin, 90);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.8);
  doc.setTextColor(75, 85, 99);
  doc.text("Solo se enlistan oficinas con material solicitado para surtido o compra consolidada.", margin, 95);
}

function getColumns(usableW) {
  const col = {
    n: 7,
    oficina: 48,
    modelo: 25,
    estado: 27,
    limitante: 23,
    pedido: 71,
    obs: usableW - 7 - 48 - 25 - 27 - 23 - 71,
  };

  return col;
}

function getColumnX(tableX, col) {
  return {
    n: tableX,
    oficina: tableX + col.n,
    modelo: tableX + col.n + col.oficina,
    estado: tableX + col.n + col.oficina + col.modelo,
    limitante: tableX + col.n + col.oficina + col.modelo + col.estado,
    pedido: tableX + col.n + col.oficina + col.modelo + col.estado + col.limitante,
    obs: tableX + col.n + col.oficina + col.modelo + col.estado + col.limitante + col.pedido,
  };
}

function drawTableHeader(doc, tableX, tableY, usableW, x) {
  const headH = 7;

  doc.setFillColor(31, 41, 55);
  doc.setDrawColor(31, 41, 55);
  doc.rect(tableX, tableY, usableW, headH, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(5.8);
  doc.setTextColor(255, 255, 255);
  doc.text("#", x.n + 2, tableY + 4.8);
  doc.text("OFICINA", x.oficina + 2, tableY + 4.8);
  doc.text("MODELO", x.modelo + 2, tableY + 4.8);
  doc.text("ESTADO", x.estado + 2, tableY + 4.8);
  doc.text("LIMITANTE", x.limitante + 2, tableY + 4.8);
  doc.text("MATERIAL SOLICITADO", x.pedido + 2, tableY + 4.8);
  doc.text("OBSERVACION", x.obs + 2, tableY + 4.8);

  return headH;
}

function drawRow(doc, row, index, y, rowH, tableX, usableW, col, x) {
  const isEven = index % 2 === 0;

  doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 252);
  doc.setDrawColor(229, 231, 235);
  doc.rect(tableX, y, usableW, rowH, "FD");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(5.9);
  doc.setTextColor(31, 41, 55);
  textFit(doc, String(index + 1), x.n + 2, y + 4, col.n - 3);

  doc.setFont("helvetica", "bold");
  doc.setTextColor(17, 24, 39);
  textFit(doc, row.oficina || row.nombre || row.oficinaId || "N/D", x.oficina + 2, y + 4, col.oficina - 4);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(55, 65, 81);
  textFit(doc, getPrinterLabel(row), x.modelo + 2, y + 4, col.modelo - 4);

  setEstadoStyle(doc, row.estado);
  doc.roundedRect(x.estado + 2, y + 1.2, col.estado - 4, rowH - 2.4, 1.3, 1.3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(5.4);
  textFit(doc, labelEstado(row.estado), x.estado + 4, y + 4, col.estado - 7);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(5.9);
  doc.setTextColor(31, 41, 55);
  textFit(doc, labelInsumo(row.insumoLimitante), x.limitante + 2, y + 4, col.limitante - 4);

  doc.setFont("helvetica", "bold");
  doc.setTextColor(17, 24, 39);
  textFit(doc, pedidoTexto(row), x.pedido + 2, y + 4, col.pedido - 4);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(75, 85, 99);
  textFit(doc, observacionTexto(row), x.obs + 2, y + 4, col.obs - 4);
}

function drawAuthorizationBlock(doc, margin, pageW, y) {
  doc.setDrawColor(229, 231, 235);
  doc.line(margin, y - 4, pageW - margin, y - 4);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.5);
  doc.setTextColor(75, 85, 99);
  doc.text("VALIDACION OPERATIVA", margin, y);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.2);
  doc.setTextColor(107, 114, 128);

  doc.text("Solicita / revisa", margin, y + 11);
  doc.line(margin + 34, y + 10, margin + 90, y + 10);

  doc.text("Autoriza", margin + 103, y + 11);
  doc.line(margin + 126, y + 10, margin + 182, y + 10);

  doc.text("Recibe proveedor / almacen", margin + 195, y + 11);
  doc.line(margin + 260, y + 10, margin + 278, y + 10);
}

function drawFooter(doc, margin) {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(5.9);
  doc.setTextColor(107, 114, 128);
  doc.text(
    "Documento generado automaticamente con base en la ultima captura de stock operativo. Uso interno para solicitud, autorizacion y surtido de insumos.",
    margin,
    202
  );
}

function drawPageNumber(doc, pageW, pageH, current, total) {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6);
  doc.setTextColor(107, 114, 128);
  doc.text(`Pagina ${current} de ${total}`, pageW - 28, pageH - 7);
}

export async function exportInsumosProveedorPdf({
  requestRows = [],
  totals: providedTotals = null,
  leadTimeDays = 4,
  safetyPercent = 0.1,
  folio = "",
  traceability = {},
} = {}) {
  const rows = requestRows
    .filter(tienePedido)
    .slice()
    .sort((a, b) => {
      const p = prioridad(a) - prioridad(b);
      if (p !== 0) return p;
      return String(a.oficina || "").localeCompare(String(b.oficina || ""));
    });

  const calculatedTotals = rows.reduce(
    (acc, row) => {
      const pedido = getPedido(row);
      acc.oficinas += 1;
      acc.plasticos += Number(pedido.plasticos || 0);
      acc.ribbon += Number(pedido.ribbon || 0);
      acc.laminador += Number(pedido.laminador || 0);
      acc.retransfer += Number(pedido.retransfer || 0);
      acc.rollos += Number(pedido.rollos || 0);
      return acc;
    },
    {
      oficinas: 0,
      plasticos: 0,
      ribbon: 0,
      laminador: 0,
      retransfer: 0,
      rollos: 0,
    }
  );

  const totals = providedTotals
    ? {
        ...calculatedTotals,
        ...providedTotals,
        oficinas: calculatedTotals.oficinas,
      }
    : calculatedTotals;

  const finalFolio = folio || defaultFolio();

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  const pageW = 297;
  const pageH = 210;
  const margin = 9;
  const usableW = pageW - margin * 2;

  const tableX = margin;
  const col = getColumns(usableW);
  const x = getColumnX(tableX, col);
  const rowH = 6.4;
  const firstPageTableY = 101;
  const continuationTableY = 52;
  const firstPageBottomLimit = 183;
  const continuationBottomLimit = 188;
  const authBlockHeight = 20;

  function startFirstPage() {
    drawPageBackground(doc, pageW, pageH);
    drawMainBanner(doc, pageW, margin, finalFolio, false);
    drawFirstPageMeta(doc, margin, usableW, totals, leadTimeDays, safetyPercent, traceability);
    const headH = drawTableHeader(doc, tableX, firstPageTableY, usableW, x);
    return firstPageTableY + headH;
  }

  function startContinuationPage() {
    doc.addPage();
    drawPageBackground(doc, pageW, pageH);
    drawMainBanner(doc, pageW, margin, finalFolio, true);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(127, 29, 29);
    doc.text("DETALLE POR OFICINA", margin, 40);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.8);
    doc.setTextColor(75, 85, 99);
    doc.text("Continuacion del listado de oficinas con material solicitado.", margin, 45);

    const headH = drawTableHeader(doc, tableX, continuationTableY, usableW, x);
    return continuationTableY + headH;
  }

  let currentY = startFirstPage();
  let currentPageIsFirst = true;

  if (!rows.length) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(75, 85, 99);
    doc.text("No hay oficinas con material solicitado en este momento.", margin, currentY + 10);
    currentY += 16;
  } else {
    rows.forEach((row, index) => {
      const bottomLimit = currentPageIsFirst ? firstPageBottomLimit : continuationBottomLimit;

      if (currentY + rowH > bottomLimit) {
        currentY = startContinuationPage();
        currentPageIsFirst = false;
      }

      drawRow(doc, row, index, currentY, rowH, tableX, usableW, col, x);
      currentY += rowH;
    });
  }

  const authStartY = currentY + 8;
  const availableBottom = pageH - 14;

  if (authStartY + authBlockHeight > availableBottom) {
    doc.addPage();
    drawPageBackground(doc, pageW, pageH);
    drawMainBanner(doc, pageW, margin, finalFolio, true);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(127, 29, 29);
    doc.text("CIERRE Y VALIDACION", margin, 40);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.8);
    doc.setTextColor(75, 85, 99);
    doc.text("Espacio reservado para validacion operativa del documento.", margin, 45);

    drawAuthorizationBlock(doc, margin, pageW, 60);
    drawFooter(doc, margin);
  } else {
    drawAuthorizationBlock(doc, margin, pageW, authStartY);
    drawFooter(doc, margin);
  }

  const totalPages = doc.getNumberOfPages();

  for (let i = 1; i <= totalPages; i += 1) {
    doc.setPage(i);
    drawPageNumber(doc, pageW, pageH, i, totalPages);
  }

  doc.save(`requisicion-insumos-pvc-${dateStamp()}.pdf`);
}



